/**
 * Infiltration Run — event card + choice row builders + final extraction prompt.
 * Pure HTML string builders. DOM binding lives in infiltrationScreen.ts.
 */

import { t } from '../i18n/index.js';
import { computeSuccessRate, finalSuccessChance, getInfilEvent, getInfilChoice } from '../game/index.js';
import type { InfilChoice, InfilEvent, InfiltrationRunState } from '../game/index.js';
import { detectionGaugeHtml, statLabel, detectionBand } from './infiltrationRender.js';

/** Illustration block: real event art if available, diamond placeholder otherwise. */
export function illustHtml(imagePath: string | undefined, variant: string): string {
  if (imagePath) {
    return `<div class="infil-illust ${variant} has-img" style="background-image:url('${imagePath}')"></div>`;
  }
  return `<div class="infil-illust ${variant}"><span class="infil-illust-mark"></span></div>`;
}

/** Agent stat value a choice reads (0 for 'none'). */
function agentStatFor(run: InfiltrationRunState, choice: InfilChoice): number {
  return choice.statType === 'none' ? 0 : run.agentStats[choice.statType];
}

/** Success rate bucket for colouring the big percentage. */
function rateClass(rate: number): string {
  if (rate >= 70) return 'good';
  if (rate >= 40) return 'mid';
  return 'bad';
}

/** One selectable choice row. */
function choiceRowHtml(run: InfiltrationRunState, choice: InfilChoice, index: number): string {
  const stat = agentStatFor(run, choice);
  const rate = computeSuccessRate(choice, stat);
  const judged = choice.statType !== 'none';
  const det = choice.statType === 'none' ? '0' : `+${choice.detectionOnSuccess}/+${choice.detectionOnFail}`;
  return `<button class="infil-choice tag-${choice.tag}" data-choice="${choice.id}">
      <div class="infil-choice-num">${index + 1}</div>
      <div class="infil-choice-main">
        <div class="infil-choice-title">${t(choice.labelKey)}</div>
        <div class="infil-choice-desc">${t(choice.descKey)}</div>
        <div class="infil-choice-meta">
          <span class="infil-meta-stat">${statLabel(choice.statType)}${judged ? ` ${stat}` : ''}</span>
          ${judged ? `<span class="infil-meta-diff">${t('infil_difficulty')} ${choice.difficulty}</span>` : ''}
          <span class="infil-meta-det">${t('infil_detection')} ${det}</span>
        </div>
      </div>
      <div class="infil-choice-right">
        <div class="infil-choice-rate ${rateClass(rate)}">${rate}%</div>
        <div class="infil-tag-badge tag-${choice.tag}">${t(`infil_tag_${choice.tag}`)}</div>
      </div>
      <div class="infil-choice-hover">${t('infil_selecting')}</div>
    </button>`;
}

/** The list of clickable choices for the current event. */
function choicesHtml(run: InfiltrationRunState, ev: InfilEvent): string {
  return `<div class="infil-choices">${ev.choices.map((c, i) => choiceRowHtml(run, c, i)).join('')}</div>`;
}

/** Reveal panel shown after a choice is made (success/fail + Next). */
function revealHtml(run: InfiltrationRunState, ev: InfilEvent): string {
  const res = run.lastChoiceResult;
  if (!res) return '';
  const choice = getInfilChoice(ev.id, res.choiceId);
  if (!choice) return '';
  const rate = computeSuccessRate(choice, agentStatFor(run, choice));
  const detAdd = res.success ? choice.detectionOnSuccess : choice.detectionOnFail;
  const isLast = run.currentIndex + 1 >= run.eventQueue.length;
  return `<div class="infil-reveal ${res.success ? 'ok' : 'bad'}">
      <div class="infil-reveal-stamp">${res.success ? t('infil_choice_success') : t('infil_choice_fail')}</div>
      <div class="infil-reveal-choice">${t(choice.labelKey)}</div>
      ${res.roll >= 0 ? `<div class="infil-reveal-roll">${t('infil_roll')} ${res.roll} / ${rate}%</div>` : ''}
      <div class="infil-reveal-detadd">${t('infil_detection')} +${detAdd}</div>
      <button class="infil-next-btn" id="infil-next">${isLast ? t('infil_to_extraction') : t('infil_next')}</button>
    </div>`;
}

/** Full event screen: gauge + situation card + choices or reveal. */
export function eventScreenHtml(run: InfiltrationRunState, gaugeStartPct: number): string {
  const ev = getInfilEvent(run.eventQueue[run.currentIndex]);
  if (!ev) return `<div class="infil-screen"><div class="infil-event">${t('infil_close')}</div></div>`;
  const progress = `${run.currentIndex + 1} / ${run.eventQueue.length}`;
  const body = run.lastChoiceResult ? revealHtml(run, ev) : choicesHtml(run, ev);
  return `<div class="infil-screen">
      ${detectionGaugeHtml(gaugeStartPct)}
      <div class="infil-event">
        <div class="infil-event-progress">${t('infil_step')} ${progress}</div>
        <div class="infil-eyebrow">${t(ev.categoryKey)}</div>
        <div class="infil-event-title">${t(ev.titleKey)}</div>
        ${illustHtml(ev.imagePath, 'infil-illust-event')}
        <div class="infil-event-desc">${t(ev.descKey)}</div>
        ${body}
      </div>
    </div>`;
}

/** Final extraction prompt (all events done, awaiting resolve). */
export function extractionScreenHtml(run: InfiltrationRunState, gaugeStartPct: number): string {
  const chance = finalSuccessChance(run.detection);
  return `<div class="infil-screen">
      ${detectionGaugeHtml(gaugeStartPct)}
      <div class="infil-event infil-extraction">
        <div class="infil-eyebrow">${t('infil_extraction_cat')}</div>
        <div class="infil-event-title">${t('infil_extraction_title')}</div>
        <div class="infil-illust infil-illust-event"><span class="infil-illust-mark"></span></div>
        <div class="infil-event-desc">${t('infil_extraction_desc')}</div>
        <div class="infil-extraction-chance band-${detectionBand(run.detection)}">
          ${t('infil_final_chance')}: <b>${chance}%</b>
        </div>
        <button class="infil-start-btn" id="infil-extract">${t('infil_attempt_extraction')}</button>
      </div>
    </div>`;
}
